/**
 * A button, in one of the app's few kinds (see /styleguide): `secondary` (the
 * default) for most, `primary` for a screen's main action, `plain` for one in
 * a line of text or a sheet's header. With `href`, a link drawn as a button.
 */
import * as preact from "preact";

import { cn } from "shared/utils";

export type ButtonKind = "primary" | "secondary" | "plain";

type Props = Omit<
  preact.JSX.ButtonHTMLAttributes<HTMLButtonElement>,
  "icon" | "size" | "type"
> & {
  kind?: ButtonKind;
  /** `large` in a form (the height of its fields) */
  size?: "medium" | "large";
  /** An icon font class (e.g. "icon-filter"), before its label */
  icon?: string;
  /** After its label: a chevron, for a button opening a menu or panel */
  chevron?: "down" | "up";
  /** Deletes or removes something */
  destructive?: boolean;
  /** An option that's on (e.g. a filter in use) */
  active?: boolean;
  /** Fills its row */
  block?: boolean;
  href?: string;
  type?: "button" | "submit";
};

export default class Button extends preact.Component<Props> {
  render() {
    const {
      kind = "secondary",
      size = "medium",
      icon,
      chevron,
      destructive,
      active,
      block,
      href,
      type = "button",
      class: className,
      children,
      ...attributes
    } = this.props;
    const iconOnly = Boolean(icon) && children == null;
    const classes = cn({
      button: true,
      [kind]: true,
      large: size === "large",
      iconOnly,
      destructive,
      active,
      block,
      [String(className ?? "")]: Boolean(className),
    });
    const content = (
      <>
        {icon ? <i class={icon} aria-hidden="true"></i> : null}
        {children}
        {chevron ? (
          <i class={`icon-angle-${chevron}`} aria-hidden="true"></i>
        ) : null}
      </>
    );
    return href ? (
      <a {...(attributes as object)} class={classes} href={href}>
        {content}
      </a>
    ) : (
      <button {...(attributes as object)} class={classes} type={type}>
        {content}
      </button>
    );
  }
}
